import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';
import { from, of } from 'rxjs';
import { catchError, map, switchMap, timeout } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

import { BsCacheService } from '@brightside-web/desktop/data-access/core-services';
import {
  HAS_CHECKED_ELIGIBILITY_DATA_COLLECT_KEY,
  RequiredVerificationService,
} from '@brightside-web/desktop/data-access/shared';

import { isLoginLegalDocsPath } from './login-legal-docs-path';

/**
 * Upper bound on the check. `MobileStateService.get()` has a path that neither emits nor
 * errors (a concurrent caller's fetch failing leaves waiters filtering on a subject that is
 * never pushed), and this gates route matching, so without it a stalled request deadlocks
 * navigation with no error to report.
 */
const ELIGIBILITY_CHECK_TIMEOUT_MS = 8000;

/**
 * Sends a signed-in client to `/verification/eligibility` while the eligibility data-collect
 * API still has fields to collect (e.g. last four SSN).
 *
 * Runs as a `canMatch`, ordered before `onboardingMatchCompleteGuard`: that guard redirects
 * to `/onboarding` at match time, so a `canActivate` on the feed route never ran for a client
 * part-way through onboarding. Those clients reached credit setup with no last four SSN on
 * record and identity verification then failed (MW-10702). Returns a `UrlTree` rather than
 * `false` so the router follows the redirect instead of falling through to the `**` route.
 *
 * Deliberately fails open — see `docs/NOTES.md`. Blocking sign-in is worse than a missed
 * check, and the cache key is only written on a definite answer, so a failure retries on the
 * next navigation instead of being skipped for the lifetime of the cache entry.
 */
export const eligibilityDataCollectMatchGuard: CanMatchFn = (route, segments) => {
  if (isLoginLegalDocsPath(segments)) {
    return of(true);
  }

  const bsCacheService = inject(BsCacheService);
  const requiredVerificationService = inject(RequiredVerificationService);
  const router = inject(Router);

  return from(bsCacheService.getItem(HAS_CHECKED_ELIGIBILITY_DATA_COLLECT_KEY)).pipe(
    switchMap((alreadyChecked) => {
      if (alreadyChecked) {
        return of(true);
      }

      // replay, skipCreation, postLogin, skipMobileState. The last argument is load-bearing:
      // without it this guard calls MobileStateService.get(), which flags itself in-flight on
      // call and only clears that flag when its stream emits. The timeout below unsubscribes
      // first, so the flag stays set and every later get() — HasTermsUpdateGuard on the feed's
      // canActivate among them — parks forever and the client sits on a spinner (MW-10702).
      // Nothing is submitted from here, and the post-login fetch omits employment_type, so the
      // mobile-state lookup this skips was never needed.
      return requiredVerificationService.getRequiredVerifications(true, false, true, true).pipe(
        map((verifications) => {
          if (verifications && verifications.length > 0) {
            return router.parseUrl('/verification/eligibility');
          }

          void bsCacheService.setItem(HAS_CHECKED_ELIGIBILITY_DATA_COLLECT_KEY, true).catch(() => undefined);
          return true;
        }),
      );
    }),
    // Bounds the cache read as well as the request, and catches both: BsCacheService.getItem
    // re-rejects, and an unhandled rejection here would fail the navigation outright rather
    // than fail open. Reported at warning level because Angular subscribes every canMatch
    // guard concurrently, so this fires once per client during an outage even when an earlier
    // guard is the one that wins the redirect.
    timeout(ELIGIBILITY_CHECK_TIMEOUT_MS),
    catchError((error) => {
      Sentry.captureException(error, {
        level: 'warning',
        tags: { guard: 'eligibility-data-collect' },
      });
      return of(true);
    }),
  );
};
